import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import { listAllocatedAdmissions, getLedgerAudit } from "./ipd-billing/billing-api";
import { listDischargeProcesses, advanceDischargeProcess } from "./ipd/api/dischargeProcessApi";
import { downloadNoDueCertificate } from "./ipd/utils/noDueCertificatePdf";
import { viewFinalizedSummary } from "./ipd/utils/viewFinalizedSummary";

// Outstanding balance for one admission, from the billing ledger's summary
// cards (net_outstanding arrives as a string). null = we couldn't read it, which
// is NOT the same as zero and must never be shown as "settled".
async function fetchOutstanding(admissionId) {
  try {
    const res = await getLedgerAudit(admissionId);
    const raw = res?.cards?.net_outstanding;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

// ─── helpers ──────────────────────────────────────────────────────────────────
function fmtWait(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

// ─── ClearDischargeModal ──────────────────────────────────────────────────────
function ClearDischargeModal({ patient, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  // An outstanding balance BLOCKS clearance unless the admin gives a reason.
  // Clearing billing is what issues the No-Due Certificate, and a certificate
  // asserting the patient owes nothing must not be issued to a patient who owes
  // something — not silently, at least. The override exists because real
  // facilities do discharge on a waiver, an offline settlement, or a pending
  // insurance claim; the difference is that the reason is now PERSISTED on the
  // discharge process, not a checkbox that evaporates.
  //
  // This replaces a tick-box that was recorded nowhere.
  const [overrideReason, setOverrideReason] = useState("");
  const hasDues = typeof patient.outstanding === "number" && patient.outstanding > 0;
  const balanceUnknown = patient.outstanding == null;
  const needsReason = hasDues || balanceUnknown;
  const blocked = needsReason && !overrideReason.trim();

  async function handleConfirm() {
    setBusy(true);
    try {
      // Advances billing -> nursing_discharge (NOT to physical discharge: the
      // nurse does that). Backend gates on actor role AND
      // summary_status === "approved", and independently recomputes the
      // outstanding balance — our `patient.outstanding` is display only. It
      // rejects the clearance if the patient owes money and no reason is given,
      // and refuses outright if it cannot read the ledger.
      const res = await advanceDischargeProcess(patient.id, {
        noDueOverrideReason: overrideReason.trim() || undefined,
      });
      if (res?.status === "Success") {
        toast.success(
          res.data?.no_due_certificate_number
            ? `Billing cleared. No-Due Certificate ${res.data.no_due_certificate_number} issued.`
            : res.message || "Billing cleared."
        );
        onConfirm(res.data);
      } else {
        toast.error(res?.message || "Could not clear this discharge.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Could not clear this discharge."
      );
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: C.card, borderRadius: 14, width: 440,
        maxWidth: "90vw", padding: 28,
        boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
        animation: "ipdFadeIn 0.15s ease",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.text }}>
              Confirm Discharge Clearance
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>
              {patient.name} &middot; UHID: {patient.uhid}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ border: "none", background: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}
          >
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{
          padding: "12px 16px", borderRadius: 8,
          background: hasDues ? "#fef2f2" : C.accent,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: hasDues || balanceUnknown ? 12 : 18,
        }}>
          <span style={{ fontSize: 13, color: C.textSub }}>Outstanding Balance</span>
          <span style={{
            fontSize: 20, fontWeight: 700,
            color: hasDues ? C.danger : balanceUnknown ? C.textSub : C.primary,
          }}>
            {balanceUnknown ? "Unavailable" : fmtINR(patient.outstanding)}
          </span>
        </div>

        {needsReason ? (
          <div style={{
            padding: "12px 16px", borderRadius: 8, marginBottom: 18,
            border: `1.5px solid ${hasDues ? C.danger : C.border}`,
            background: hasDues ? "#fff7f7" : "#f9fafb",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
              {hasDues
                ? "This patient still owes money, so a No-Due Certificate cannot be issued cleanly. To clear anyway, state why — the reason is recorded on the discharge and printed on the certificate."
                : "The outstanding balance could not be read from the ledger. To clear anyway, state why."}
            </p>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textSub, letterSpacing: "0.05em", marginBottom: 6 }}>
              REASON FOR OVERRIDE (required)
            </label>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              placeholder="e.g. balance waived by management; settled in cash at counter; insurance claim pending"
              style={{
                width: "100%", boxSizing: "border-box", padding: "10px 12px",
                border: `1.5px solid ${C.border}`, borderRadius: 8,
                fontSize: 13, color: C.text, resize: "vertical",
                fontFamily: "inherit", outline: "none",
              }}
            />
          </div>
        ) : (
          <div style={{
            padding: "12px 16px", borderRadius: 8, marginBottom: 18,
            border: `1.5px solid ${C.border}`, background: "#f9fafb",
          }}>
            <p style={{ margin: 0, fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
              No dues outstanding. Clearing issues a No-Due Certificate and hands
              the patient to the nurse for physical discharge.
            </p>
          </div>
        )}

        {!patient.summaryApproved && (
          <div style={{
            padding: "12px 16px", borderRadius: 8, marginBottom: 18,
            border: `1.5px solid ${C.danger}`, background: "#fff7f7",
          }}>
            <p style={{ margin: 0, fontSize: 12.5, color: C.danger, lineHeight: 1.5 }}>
              The doctor has not approved the discharge summary yet. Clearing will
              be rejected until they do.
            </p>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px", border: `1.5px solid ${C.border}`,
              borderRadius: 8, background: "transparent", color: C.text,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || blocked}
            title={blocked ? "Give a reason for the override to continue" : undefined}
            style={{
              padding: "9px 20px", border: "none", borderRadius: 8,
              background: C.success, color: "#fff",
              fontSize: 13, fontWeight: 600,
              cursor: busy || blocked ? "not-allowed" : "pointer",
              opacity: busy || blocked ? 0.5 : 1,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <SvgIcon name="check" size={14} color="#fff" />
            {busy ? "Clearing…" : "Clear & Issue No-Due Certificate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
export default function IPDBillingDischarge() {
  const [queue, setQueue]             = useState(null); // enriched billing-stage rows; null = loading
  const [loadError, setLoadError]     = useState(false); // distinct from a genuinely-empty queue
  const [clearTarget, setClearTarget] = useState(null);

  // Billing-stage queue = DischargeProcess rows (bare — no patient/ward) left-
  // joined against the admissions list by admission_id. Also drives the
  // pending/avgWait KPIs below, since both are computed from these same rows.
  const loadQueue = useCallback(async () => {
    try {
      const [processRows, admissionRows] = await Promise.all([
        listDischargeProcesses({ stage: "billing" }),
        listAllocatedAdmissions(),
      ]);

      const admissionById = new Map();
      (admissionRows || []).forEach((row) => {
        const p = row?.patient || {};
        admissionById.set(String(row.admission_id), {
          name:
            p.name ||
            [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
            "",
          // department_id is an opaque "<slug>_<facility_id>" key, not a label;
          // fall back to it only when the bed has no room/ward.
          ward: row.ward_name || row.department_id || "",
          uhid: p.uhid || p.patient_id || "",
        });
      });

      const now = Date.now();
      const enriched = (processRows || []).map((dp) => {
        const admission = admissionById.get(String(dp.admission_id));
        // Billing is now entered straight from Ward (the Admission Office
        // stage was collapsed), so the doctor's ward_completed_at stamp is
        // when this row started waiting on us.
        const enteredBillingAt = dp.ward_completed_at
          ? new Date(dp.ward_completed_at).getTime()
          : null;
        const waitMinutes =
          enteredBillingAt != null && !Number.isNaN(enteredBillingAt)
            ? Math.max(0, Math.round((now - enteredBillingAt) / 60000))
            : null;
        return {
          id: dp.id,
          admissionId: dp.admission_id,
          // Admission not found in the join: show the admission_id rather
          // than crash or silently drop the row.
          name: admission?.name || `Admission #${dp.admission_id}`,
          ward: admission?.ward || "—",
          uhid: admission?.uhid || "—",
          waitMinutes,
          summaryApproved: dp.summary_status === "approved",
        };
      });

      // The outstanding balance is the whole point of a billing closure, and it
      // was never fetched — the confirm modal rendered fmtINR(undefined) and
      // showed "—" every time, so the admin was clearing discharges with no
      // financial information at all. One ledger read per queued row; the queue
      // is small (it's a worklist, not a report).
      const balances = await Promise.all(
        enriched.map((row) => fetchOutstanding(row.admissionId))
      );
      enriched.forEach((row, i) => {
        row.outstanding = balances[i];
      });

      setQueue(enriched);
      setLoadError(false);
    } catch (err) {
      console.error("Discharge billing queue load failed:", err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load discharge queue."
      );
      setQueue([]);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // The cleared row leaves this queue at once (it moves to the nurse's
  // nursing_discharge stage), so this is the admin's one moment to take the
  // certificate — hand it to them rather than making them go hunting for it.
  // The document is rendered from the snapshot the backend just froze, so it is
  // reproducible later from the same row if anyone needs it again.
  function handleCleared(clearedProcess) {
    const patient = clearTarget;
    setClearTarget(null);
    loadQueue();

    if (!clearedProcess?.no_due_certificate_number) return;
    try {
      downloadNoDueCertificate(clearedProcess, {
        name: patient?.name,
        uhid: patient?.uhid,
      });
    } catch (err) {
      // The clearance itself succeeded — a failed PDF render must not read as a
      // failed discharge.
      console.error("No-due certificate render failed:", err);
      toast.warn("Billing was cleared, but the certificate could not be generated.");
    }
  }

  const visibleQueue = queue || [];
  const isLoading     = queue === null;

  const pending = queue && !loadError ? queue.length : null;
  const knownWaits = queue ? queue.filter((p) => p.waitMinutes != null) : [];
  const avgWaitMinutes =
    queue && knownWaits.length > 0
      ? Math.round(knownWaits.reduce((sum, p) => sum + p.waitMinutes, 0) / knownWaits.length)
      : null;

  const KPI_CARDS = [
    { label: "PENDING DISCHARGES", value: pending ?? "—", bg: "#fffbeb", color: "#92400e" },
    { label: "AVG. WAIT TIME",     value: avgWaitMinutes != null ? fmtWait(avgWaitMinutes) : "—", bg: "#f0f9ff", color: "#0c4a6e" },
    // pending bill-preview integration — no backend source for a cleared-today count yet.
    { label: "TODAY CLEARED",      value: "—", bg: "#f0fdf4", color: "#14532d" },
    // pending bill-preview integration — no SLA threshold/breach data yet.
    { label: "SLA BREACHES",       value: "—", bg: "#fef2f2", color: C.danger   },
  ];

  return (
    <IPDBillingLayout activeNav="discharge">
      <style>{`
        .dwr-patient-card { transition: box-shadow 0.15s; }
        .dwr-patient-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08) !important; }
        .dwr-clear:hover { filter: brightness(1.15); }
      `}</style>

      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 22 }}>

        {/* ── page header ──────────────────────────────────────────────────── */}
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 10 }}>
            <SvgIcon name="alert-triangle" size={22} color={C.danger} />
            Discharge War Room
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>
            Real-time coordination to clear discharges fast
          </p>
        </div>

        {/* ── KPI strip ────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 14 }}>
          {KPI_CARDS.map((card) => (
            <div
              key={card.label}
              style={{
                flex: 1, padding: "18px 22px", borderRadius: 10,
                background: card.bg, border: `1px solid ${C.border}`,
              }}
            >
              <div style={{
                fontSize: 11, fontWeight: 600, letterSpacing: "0.07em",
                color: card.color, marginBottom: 10,
              }}>
                {card.label}
              </div>
              {isLoading ? (
                <div style={{
                  height: 30, width: "55%", borderRadius: 4,
                  background: "linear-gradient(90deg, rgba(0,0,0,0.07) 25%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.07) 75%)",
                  backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                }} />
              ) : (
                <div style={{ fontSize: 30, fontWeight: 700, color: card.color, lineHeight: 1 }}>
                  {card.value}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── main 2-col area ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

          {/* Active Queue ───────────────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12,
            }}>
              {/* card header */}
              <div style={{
                padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Active Queue</span>
                {!isLoading && (
                  <span style={{
                    padding: "2px 10px", borderRadius: 12,
                    background: C.accent, color: C.primary,
                    fontSize: 12, fontWeight: 600,
                  }}>
                    {visibleQueue.length} pending
                  </span>
                )}
              </div>

              {/* patient cards */}
              <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                {isLoading ? (
                  [1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: 120, borderRadius: 10,
                        background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                        backgroundSize: "200% 100%", animation: "ipdShimmer 1.4s infinite",
                      }}
                    />
                  ))
                ) : loadError ? (
                  <div style={{ padding: 48, textAlign: "center", color: C.textSub, fontSize: 14 }}>
                    <SvgIcon name="alert-triangle" size={32} color={C.danger} />
                    <p style={{ marginTop: 12 }}>Couldn&rsquo;t load the discharge queue.</p>
                    <button
                      onClick={loadQueue}
                      style={{
                        marginTop: 12, padding: "7px 20px", border: "none",
                        borderRadius: 8, background: C.primary, color: "#fff",
                        fontSize: 13, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : visibleQueue.length === 0 ? (
                  <div style={{ padding: 48, textAlign: "center", color: C.textSub, fontSize: 14 }}>
                    <SvgIcon name="check" size={32} color={C.success} />
                    <p style={{ marginTop: 12 }}>All discharges cleared for now.</p>
                  </div>
                ) : (
                  visibleQueue.map((p) => (
                    <div
                      key={p.id}
                      className="dwr-patient-card"
                      style={{
                        padding: "16px 18px", borderRadius: 10,
                        border: `1px solid ${C.border}`,
                        background: C.card,
                      }}
                    >
                      {/* name + wait */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{p.name}</span>
                          <span style={{ fontSize: 13, color: C.textSub, marginLeft: 8 }}>· {p.ward}</span>
                        </div>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 5,
                          fontSize: 13, fontWeight: 500,
                          color: C.textSub,
                        }}>
                          <SvgIcon name="clock" size={14} color={C.textSub} />
                          {p.waitMinutes != null ? fmtWait(p.waitMinutes) : "—"}
                        </div>
                      </div>

                      {/* UHID */}
                      <div style={{ fontSize: 12, color: C.textSub, fontFamily: "monospace", marginBottom: 12 }}>
                        UHID: {p.uhid}
                      </div>

                      {/* Blockers, surfaced BEFORE the admin clicks Clear. The
                          summary-approval gate is enforced server-side, so a row
                          missing it would previously look actionable, fail on
                          click, and give no clue who to chase. */}
                      {!p.summaryApproved && (
                        <div style={{
                          display: "inline-block", marginBottom: 12,
                          padding: "4px 10px", borderRadius: 6,
                          background: "#fff7f7", border: `1px solid ${C.danger}`,
                          fontSize: 12, fontWeight: 600, color: C.danger,
                        }}>
                          Awaiting doctor's summary approval
                        </div>
                      )}

                      {/* outstanding + actions */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 13, color: C.textSub }}>
                          Outstanding{" "}
                          <strong style={{
                            fontSize: 15,
                            color: p.outstanding > 0 ? C.danger : C.text,
                          }}>
                            {p.outstanding == null ? "—" : fmtINR(p.outstanding)}
                          </strong>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {/* The desk is blocked BY this document and had no way
                              to read it — only a red pill saying it wasn't
                              approved yet. */}
                          <button
                            onClick={() => viewFinalizedSummary(p.admissionId)}
                            style={{
                              padding: "7px 16px", border: `1.5px solid ${C.border}`,
                              borderRadius: 8, background: "transparent",
                              color: C.text, fontSize: 13, fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            View Summary
                          </button>
                          <button
                            className="dwr-clear"
                            onClick={() => setClearTarget(p)}
                            style={{
                              padding: "7px 16px", border: "none",
                              borderRadius: 8, background: C.primary,
                              color: "#fff", fontSize: 13, fontWeight: 600,
                              cursor: "pointer",
                              display: "flex", alignItems: "center", gap: 6,
                              transition: "filter 0.15s",
                            }}
                          >
                            <SvgIcon name="check" size={14} color="#fff" />
                            Clear Discharge
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right panel ────────────────────────────────────────────────── */}
          <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Department Coordination: pending bill-preview integration — no
                get_department_status/-equivalent endpoint exists yet, so this
                is a static placeholder rather than mock data. */}
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: "18px 20px",
            }}>
              <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: C.text }}>
                Department Coordination
              </h4>
              <p style={{ margin: 0, fontSize: 12.5, color: C.textSub, lineHeight: 1.5 }}>
                Department-level coordination status isn't available yet.
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Clear Discharge Modal */}
      {clearTarget && (
        <ClearDischargeModal
          patient={clearTarget}
          onClose={() => setClearTarget(null)}
          onConfirm={handleCleared}
        />
      )}
    </IPDBillingLayout>
  );
}
